import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  CheckIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const NotificationActions = ({
  unreadCount = 0,
  totalCount = 0,
  onMarkAllAsRead,
  onClearAll,
  isLoading = false
}) => {
  const intl = useIntl();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);

  const handleMarkAllAsRead = () => {
    if (onMarkAllAsRead && unreadCount > 0) {
      onMarkAllAsRead();
    }
    setIsMenuOpen(false);
  };

  const handleClearAll = () => {
    setShowClearConfirmation(true);
    setIsMenuOpen(false);
  };

  const confirmClearAll = () => {
    if (onClearAll) {
      onClearAll();
    }
    setShowClearConfirmation(false);
  };

  const cancelClearAll = () => {
    setShowClearConfirmation(false);
  };

  return (
    <div className="relative">
      {/* Actions Menu Button */}
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        disabled={isLoading || totalCount === 0}
        className="p-2 text-succession-text-secondary hover:text-succession-text-primary hover:bg-[var(--succession-hover-bg)] rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
        aria-label="Notification actions"
      >
        <EllipsisVerticalIcon className="w-5 h-5" />
      </button>

      {/* Dropdown Menu */}
      {isMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsMenuOpen(false)}
          />
          
          {/* Menu */}
          <div className="absolute right-0 top-full mt-2 w-56 bg-succession-bg-primary rounded-lg shadow-succession-lg border border-succession-border z-20">
            <div className="py-2">
              {/* Mark All as Read */}
              <button
                onClick={handleMarkAllAsRead}
                disabled={unreadCount === 0 || isLoading}
                className="w-full flex items-center px-4 py-2 text-sm text-succession-text-primary hover:bg-[var(--succession-hover-bg)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
              >
                <CheckIcon className="w-4 h-4 mr-3 text-succession-success" />
                <div className="text-left">
                  <div>{intl.formatMessage({ id: 'succession.notifications.mark_all_as_read' })}</div>
                  <div className="text-xs text-succession-text-muted">
                    {unreadCount > 0 ? intl.formatMessage({ id: 'succession.notifications.unread_count' }, { count: unreadCount }) : intl.formatMessage({ id: 'succession.notifications.no_unread' })}
                  </div>
                </div>
              </button>

              {/* Divider */}
              <div className="border-t border-succession-border my-1" />

              {/* Clear All Notifications */}
              <button
                onClick={handleClearAll}
                disabled={totalCount === 0 || isLoading}
                className="w-full flex items-center px-4 py-2 text-sm text-succession-error hover:bg-succession-error-bg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
              >
                <TrashIcon className="w-4 h-4 mr-3 text-succession-error" />
                <div className="text-left">
                  <div>{intl.formatMessage({ id: 'succession.notifications.clear_all' })}</div>
                  <div className="text-xs text-succession-text-muted">
                    {totalCount > 0 ? intl.formatMessage({ id: 'succession.notifications.remove_all' }, { count: totalCount }) : intl.formatMessage({ id: 'succession.notifications.no_notifications_to_clear' })}
                  </div>
                </div>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Clear Confirmation Modal */}
      {showClearConfirmation && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            {/* Modal */}
            <div className="bg-succession-bg-primary rounded-lg shadow-succession-xl max-w-md w-full p-6">
              {/* Icon */}
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-succession-error-bg rounded-full">
                <ExclamationTriangleIcon className="w-6 h-6 text-succession-error" />
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-succession-text-primary text-center mb-2">
                {intl.formatMessage({ id: 'succession.notifications.clear_all_title' })}
              </h3>

              {/* Description */}
              <p className="text-sm text-succession-text-secondary text-center mb-6">
                {intl.formatMessage({ id: 'succession.notifications.clear_all_body' }, { count: totalCount })}
              </p>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={cancelClearAll}
                  className="flex-1 px-4 py-2 text-sm font-medium text-succession-text-primary bg-succession-bg-primary border border-succession-border rounded-lg hover:bg-[var(--succession-hover-bg)] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                >
                  {intl.formatMessage({ id: 'succession.notifications.cancel' })}
                </button>
                <button
                  onClick={confirmClearAll}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-succession-error rounded-lg hover:bg-succession-error/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--succession-focus-ring)]"
                >
                  {isLoading ? intl.formatMessage({ id: 'succession.notifications.clear_all_clearing' }) : intl.formatMessage({ id: 'succession.notifications.clear_all_confirm' })}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

NotificationActions.propTypes = {
  unreadCount: PropTypes.number,
  totalCount: PropTypes.number,
  onMarkAllAsRead: PropTypes.func,
  onClearAll: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default NotificationActions; 